import React from 'react';
import { Handle, Position } from '@xyflow/react';
import {
  LuBot,
  LuDatabase,
  LuGlobe,
  LuWebhook,
  LuServer,
  LuCpu,
  LuHardDrive,
  LuUsers,
  LuActivity,
  LuSettings2,
  LuCheckCircle,
  LuXCircle,
  LuAlertCircle,
  LuLoader2
} from "react-icons/lu";

const DataFlowNode = ({ data }) => {
  const getIconFromValue = (iconValue) => {
    switch (iconValue) {
      case 'LuDatabase':
        return <LuDatabase className="w-6 h-6" />;
      case 'LuBot':
        return <LuBot className="w-6 h-6" />;
      case 'LuGlobe':
        return <LuGlobe className="w-6 h-6" />;
      case 'LuWebhook':
        return <LuWebhook className="w-6 h-6" />;
      case 'LuServer':
        return <LuServer className="w-6 h-6" />;
      case 'LuCpu':
        return <LuCpu className="w-6 h-6" />;
      case 'LuBraces':
        return <LuWebhook className="w-6 h-6" />; // Using webhook for LuBraces
      default:
        return <LuBot className="w-6 h-6" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'not_configured':
        return <LuAlertCircle className="w-3.5 h-3.5 text-orange-400" />;
      case 'configured':
        return <LuSettings2 className="w-3.5 h-3.5 text-blue-400" />;
      case 'success':
        return <LuCheckCircle className="w-3.5 h-3.5 text-green-400" />;
      case 'in_progress':
        return <LuLoader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />;
      case 'failed':
        return <LuXCircle className="w-3.5 h-3.5 text-red-400" />;
      case 'error':
        return <LuXCircle className="w-3.5 h-3.5 text-red-400" />;
      case 'running':
        return <LuCheckCircle className="w-3.5 h-3.5 text-green-400" />;
      case 'stopped':
        return <LuXCircle className="w-3.5 h-3.5 text-red-400" />;
      case 'idle':
        return <LuAlertCircle className="w-3.5 h-3.5 text-yellow-400" />;
      default:
        return <LuSettings2 className="w-3.5 h-3.5 text-blue-400" />;
    }
  };

  const getNodeTypeStyles = (type) => {
    switch (type) {
      case 'database':
        return {
          bg: 'bg-[#1D1D29] hover:bg-[#282834]',
          border: 'border-[#323249]',
          iconColor: 'text-blue-400',
          accent: 'bg-blue-500/20'
        };
      case 'worker':
        return {
          bg: 'bg-[#1D231D] hover:bg-[#282E28]',
          border: 'border-[#323E32]',
          iconColor: 'text-green-400',
          accent: 'bg-green-500/20'
        };
      case 'server':
        return {
          bg: 'bg-[#231D23] hover:bg-[#332633]',
          border: 'border-[#3E323E]',
          iconColor: 'text-purple-400',
          accent: 'bg-purple-500/20'
        };
      default:
        return {
          bg: 'bg-[#1e1e1e] hover:bg-[#2e2e2e]',
          border: 'border-[#ffffff10]',
          iconColor: 'text-[#e1e1e1]',
          accent: 'bg-gray-500/20'
        };
    }
  };

  const getIconForType = (type) => {
    switch (type) {
      case 'database':
        return 'LuDatabase';
      case 'worker':
        return 'LuBot';
      case 'server':
        return 'LuServer';
      default:
        return data.icon || 'LuBot';
    }
  };

  const formatStorageSize = (bytes) => {
    if (!bytes && bytes !== 0) return 'Unknown';
    if (bytes === 0) return '0 B';

    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatFirestoreStorage = (data) => {
    // For Firestore, we can show document count and estimated size
    if (data.documentCount !== undefined) {
      const docText = data.documentCount === 1 ? 'doc' : 'docs';
      if (data.estimatedSize) {
        return `${data.documentCount} ${docText} • ${formatStorageSize(data.estimatedSize)}`;
      }
      return `${data.documentCount} ${docText}`;
    }

    // Fallback to storage size if available
    if (data.storageUsed !== undefined) {
      return formatStorageSize(data.storageUsed);
    }

    return 'No data';
  };

  const getTypeSpecificContent = () => {
    switch (data.type) {
      case 'database':
        return {
          icon: <LuHardDrive className="w-4 h-4" />,
          text: data?.handle,
        };
      case 'worker':
        return {
          icon: <LuActivity className="w-4 h-4" />,
          text: data.dataDescription || data.scrapeURL || 'Not configured',
        };
      case 'server':
        return {
          icon: <LuUsers className="w-4 h-4" />,
          text: data.visibility !== undefined ? `${data.visibility}` : 'Unknown',
        };
      default:
        return null;
    }
  };

  const nodeStyles = getNodeTypeStyles(data.type);
  const iconValue = getIconForType(data.type);
  const typeSpecificContent = getTypeSpecificContent();

  // Determine if this node should have handles based on priority
  const isFirstNode = data.priority === 1 || (data.priority && data.priority <= 1);
  const isLastNode = false; // We'll assume it's not the last node unless we have more context
  const hasIncoming = !isFirstNode;
  const hasOutgoing = !isLastNode;

  // Detect panel state from data (you'll need to pass this from parent)
  const isPanelOpen = data.isPanelOpen || false;

  if (data.status === "addable") {
    return (
      <div className={`w-64 h-32 p-4 border cursor-pointer rounded-xl flex flex-col justify-between relative bg-[#ffffff05] border-[#ffffff15] border-dashed hover:bg-[#ffffff10] transition`}>
        <div className="flex items-center gap-3 w-full">
          <div className={`flex-shrink-0 text-[#e1e1e1]`}>
            {getIconFromValue(iconValue)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium text-sm truncate">{data.label}</div>
            <div className="text-white/60 text-xs truncate capitalize">{data.type}</div>
            {data.inputValue && (
              <div className="text-white/80 text-xs mt-1 truncate">{data.inputValue}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {hasIncoming && (
        <Handle
          type="target"
          position={isPanelOpen ? Position.Top : Position.Left}
          style={{
            background: 'transparent',
            border: 'none',
            width: 12,
            height: 12,
            opacity: 0,
            ...(isPanelOpen ? {
              top: '-6px',
              left: '50%',
              transform: 'translateX(-50%)'
            } : {
              top: '64px',
              left: '-6px'
            })
          }}
        />
      )}

      {hasOutgoing && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            background: 'transparent',
            border: 'none',
            width: 12,
            height: 12,
            opacity: 0,
            ...(isPanelOpen ? {
              bottom: '-26px', // Position at bottom of the accent element
              left: '50%',
              transform: 'translateX(-50%)'
            } : {
              bottom: '-26px', // Position at bottom of the accent element
              left: '32px'
            })
          }}
        />
      )}

      {/* Smaller accent element with type-specific info */}
      <div className={`absolute overflow-clip flex top-20 z-0 w-64 h-20 border cursor-pointer transition rounded-xl ${nodeStyles.bg} ${nodeStyles.border}`}>
        <div className="h-full w-3 bg-gradient-to-r from-[#ffffff10] to-transparent" />
        <div className="h-full flex-1 flex items-end justify-center px-3">
          <div className={"h-8 w-full"}>
            {typeSpecificContent && (
                <div className="flex items-end h-full py-2 gap-2 text-white/70 w-full">
                  <div className={`${nodeStyles.iconColor}`}>
                    {typeSpecificContent.icon}
                  </div>
                  <div className={`text-2xs opacity-75 font-medium ${nodeStyles.iconColor} truncate w-44`}>
                    {typeSpecificContent.text}
                  </div>
                </div>
            )}
          </div>
        </div>
        <div className="h-full w-3 bg-gradient-to-l from-[#ffffff10] to-transparent"/>
      </div>

      {/* Main taller node - now positioned above */}
      <div
          className={`relative z-10 w-64 h-32 p-4 border cursor-pointer transition rounded-xl flex flex-col justify-between ${nodeStyles.bg} ${nodeStyles.border}`}>
        <div className="flex items-center gap-3 w-full">
          <div className={`flex-shrink-0 ${nodeStyles.iconColor}`}>
            {getIconFromValue(iconValue)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-medium text-sm truncate">{data.label}</div>
            <div className="text-white/60 text-xs truncate capitalize">{data.type}</div>
            {data.inputValue && (
              <div className="text-white/80 text-xs mt-1 truncate">{data.inputValue}</div>
            )}
          </div>
        </div>

        <div className={"flex items-center gap-1.5 w-full"}>
          {data.status && getStatusIcon(data.status)}
          {data.status && (
            <div className="text-white/60 text-xs capitalize">{data.status.replace('_', ' ')}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataFlowNode;